import { useCallback, useMemo } from 'react'
import { useCommunity } from '@/lib/CommunityContext'
import { useLocale } from '@/lib/LocaleContext'

/** The zone when the browser knows it; undefined otherwise (the reader's own then). */
const validZone = (zone?: string | null): string | undefined => {
  if (!zone) return undefined
  try {
    new Intl.DateTimeFormat('en-US', { timeZone: zone })
    return zone
  } catch {
    return undefined
  }
}

/** The zone's name as the reader says it ("Horário de Brasília"). */
const zoneName = (locale: string, zone: string): string => {
  for (const style of ['longGeneric', 'long'] as const) {
    try {
      const part = new Intl.DateTimeFormat(locale, { timeZone: zone, timeZoneName: style })
        .formatToParts(new Date())
        .find((p) => p.type === 'timeZoneName')
      if (part?.value) return part.value
    } catch {
      /* an older browser without this style tries the next one */
    }
  }
  return zone
}

/**
 * Sales dates in the community's time zone, never the reader's: the rule is
 * "the community country's zone, always". `community.timezone` is what the API
 * derives from the country of the business information (until F-69 stores
 * one on the community).
 */
export function useCommunityTime() {
  const { community } = useCommunity()
  const { currentLocale } = useLocale()
  const zone = validZone(community?.timezone)

  const formatters = useMemo(
    () => ({
      dateTime: new Intl.DateTimeFormat(currentLocale, { dateStyle: 'medium', timeStyle: 'short', timeZone: zone }),
      date: new Intl.DateTimeFormat(currentLocale, { dateStyle: 'medium', timeZone: zone })
    }),
    [currentLocale, zone]
  )

  const format = useCallback(
    (kind: 'dateTime' | 'date', iso?: string | null) => {
      const value = iso ? new Date(iso) : null
      return value && !Number.isNaN(value.getTime()) ? formatters[kind].format(value) : '—'
    },
    [formatters]
  )

  return {
    zone,
    /** The zone's name for a note on the screen; null when the community has none. */
    zoneLabel: zone ? zoneName(currentLocale, zone) : null,
    dateTime: (iso?: string | null) => format('dateTime', iso),
    date: (iso?: string | null) => format('date', iso)
  }
}
